/**
 * Sysadmin Tenant Detail — Overview tab (R5 §4.1 KPIs + health + lifecycle + alerts).
 *
 * Spec §4.1 [v1]: KPI cards (users, conversations 30d, storage, token
 * spend, GPU minutes), health micro-strip (api / db / qdrant / valkey),
 * recent lifecycle events, last 5 alerts.
 *
 * R5 fix (2026-05-13): adds the 3 missing KPI tiles (storage / tokens /
 * GPU), the health micro-strip, the lifecycle panel, and the alerts
 * panel. All driven from the aggregated detail GET — no extra
 * round-trips.
 */

import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users, FileText, MessageSquare, MessagesSquare, HardDrive, Coins, Cpu,
  Activity, AlertTriangle, History,
  Server, Database, Network, Zap,
  CheckCircle2, XCircle, Circle, AlertCircle,
} from 'lucide-react';
import { useTenantDetail, type HealthStatus } from '@/lib/use-tenants';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/')({
  component: TenantOverviewTab,
});

function TenantOverviewTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/' });
  const { data } = useTenantDetail(tenantId);

  if (!data) return null;
  const { stats, tenant, health, recentLifecycle, recentAlerts } = data;

  return (
    <div className="space-y-5">
      {/* ── KPI tiles ────────────────────────────────────────────── */}
      <div>
        <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
          Activity (last 30 days)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Users} label="Users" value={stats.userCount.toLocaleString()} />
          <StatTile icon={FileText} label="Documents" value={stats.documentCount.toLocaleString()} />
          <StatTile icon={MessageSquare} label="Conversations" value={stats.conversationCount.toLocaleString()} />
          <StatTile icon={MessagesSquare} label="Messages" value={stats.messageCount.toLocaleString()} />
          <StatTile
            icon={HardDrive}
            label="Storage"
            value={`${stats.storageGb.toFixed(2)} GB`}
            hint="Sum of ai_documents.file_size"
          />
          <StatTile
            icon={Coins}
            label="Tokens in (30d)"
            value={stats.tokensIn30d.toLocaleString()}
            hint="messages.metadata->tokens.input"
          />
          <StatTile
            icon={Coins}
            label="Tokens out (30d)"
            value={stats.tokensOut30d.toLocaleString()}
            hint="messages.metadata->tokens.output"
          />
          <StatTile
            icon={Cpu}
            label="GPU min (30d)"
            value={stats.gpuMinutes30d.toLocaleString()}
            hint={stats.gpuMinutes30d === 0 ? 'GPU tracking lands with model-router observability (v1.1)' : undefined}
          />
        </div>
      </div>

      {/* ── Health micro-strip ──────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Service health
          </CardTitle>
          <CardDescription>
            Live probes against the 4 services this tenant depends on.{' '}
            {health.probedAt ? (
              <>
                Probed {new Date(health.probedAt).toLocaleString()}.
              </>
            ) : (
              <>No probe rows yet — probe scheduler hasn't run.</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            <HealthTile icon={Server} label="API" status={health.api} />
            <HealthTile icon={Database} label="DB" status={health.db} />
            <HealthTile icon={Zap} label="Qdrant" status={health.qdrant} />
            <HealthTile icon={Network} label="Valkey" status={health.valkey} />
          </div>
        </CardContent>
      </Card>

      {/* ── At a glance ─────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">At a glance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <RowField label="Mode" value={tenant.mode} />
          <RowField label="Region" value={tenant.region ?? '— (inherits platform default)'} />
          <RowField label="Timezone" value={tenant.timezone ?? '— (inherits UTC)'} mono />
          <RowField label="Primary contact" value={tenant.primaryContactEmail ?? '— (not set)'} mono />
          <RowField label="Created" value={new Date(tenant.createdAt).toLocaleString()} />
          <RowField label="Last update" value={new Date(tenant.updatedAt).toLocaleString()} />
        </CardContent>
      </Card>

      {/* ── Lifecycle + alerts (two columns on wide screens) ────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              Recent lifecycle events
            </CardTitle>
            <CardDescription>
              Suspend / restore / delete / features-changed history for this tenant.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {recentLifecycle.length === 0 ? (
              <div className="px-6 py-4 text-sm text-muted-foreground">
                No lifecycle events yet.
              </div>
            ) : (
              <ul className="divide-y border-y">
                {recentLifecycle.map((ev) => (
                  <li key={ev.id} className="px-4 py-2.5 text-sm flex items-start gap-3">
                    <LifecycleIcon action={ev.action} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <code className="font-mono text-xs">{ev.action}</code>
                        <span className="text-[11px] text-muted-foreground">
                          {new Date(ev.createdAt).toLocaleString()}
                        </span>
                      </div>
                      {ev.sysadminEmail && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          by {ev.sysadminEmail}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              Recent egress blocks
            </CardTitle>
            <CardDescription>
              Blocked outbound attempts (air-gap signal, platform-wide).
              A dedicated platform_alerts table is v1.1.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {recentAlerts.length === 0 ? (
              <div className="px-6 py-4 text-sm text-muted-foreground">
                No egress blocks in the recent window.
              </div>
            ) : (
              <ul className="divide-y border-y">
                {recentAlerts.map((a) => (
                  <li key={a.id} className="px-4 py-2.5 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-mono text-xs">{a.destination}</span>
                          {a.port && (
                            <span className="text-[11px] text-muted-foreground">
                              :{a.port}
                            </span>
                          )}
                          {a.protocol && (
                            <Badge variant="outline" className="text-[10px]">{a.protocol}</Badge>
                          )}
                          {a.isTest && (
                            <Badge variant="secondary" className="text-[10px]">Test</Badge>
                          )}
                        </div>
                        {a.reason && (
                          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                            {a.reason}
                          </div>
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                        {new Date(a.blockedAt).toLocaleString()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatTile({
  icon: Icon, label, value, hint,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
        {hint && (
          <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={hint}>
            {hint}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HealthTile({
  icon: Icon, label, status,
}: {
  icon: typeof Server;
  label: string;
  status: HealthStatus;
}) {
  return (
    <div className="rounded border p-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium">{label}</div>
        <div className="text-[10px] flex items-center gap-1 mt-0.5">
          <HealthStatusBadge status={status} />
        </div>
      </div>
    </div>
  );
}

function HealthStatusBadge({ status }: { status: HealthStatus }) {
  const meta = {
    healthy: { icon: CheckCircle2, cls: 'text-emerald-600', label: 'healthy' },
    degraded: { icon: AlertCircle, cls: 'text-amber-600', label: 'degraded' },
    down: { icon: XCircle, cls: 'text-red-600', label: 'down' },
    unknown: { icon: Circle, cls: 'text-muted-foreground', label: 'unknown' },
  }[status];
  const Icon = meta.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 capitalize', meta.cls)}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function LifecycleIcon({ action }: { action: string }) {
  if (action === 'TENANT_SUSPENDED') {
    return <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />;
  }
  if (action === 'TENANT_DELETED') {
    return <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />;
  }
  if (action === 'TENANT_RESTORED' || action === 'TENANT_DELETE_RESTORED') {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />;
  }
  return <Activity className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />;
}

function RowField({ label, value, mono }: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className={cn('text-foreground', mono && 'font-mono text-xs')}>{value}</span>
    </div>
  );
}
